import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trophy, Plus, Users, Calendar, ChevronRight, Crown, Clock, Banknote, Gift } from "lucide-react";
import { motion } from "framer-motion";
import ComingSoon from "@/components/ComingSoon";
import TournamentForm from "@/components/TournamentForm";

export const TOURNAMENT_STATUS = {
  open: { label: "Registration open", cls: "bg-teal/10 text-teal border-teal/20" },
  confirmed: { label: "Confirmed", cls: "bg-coral/10 text-coral border-coral/20" },
  completed: { label: "Completed", cls: "bg-golden/10 text-golden border-golden/20" },
  cancelled: { label: "Cancelled", cls: "bg-red-500/10 text-red-500 border-red-500/20" },
};

export default function Tournaments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    api.get("/tournaments").then(r => setTournaments(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (user?.role !== "admin") return <ComingSoon icon={Trophy} title="coming.tournaments" text="coming.tournaments_text" testId="tournaments-coming-soon" />;
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-coral rounded-full border-t-transparent" /></div>;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = tournaments.filter(t => t.date >= today && t.status !== "completed").sort((a, b) => a.date.localeCompare(b.date));
  const past = tournaments.filter(t => !upcoming.includes(t));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="tournaments-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-golden/10 flex items-center justify-center"><Trophy size={24} className="text-golden" /></div>
            <div>
              <h1 className="font-heading font-black text-3xl tracking-tight">Tournaments</h1>
              <p className="text-muted-foreground text-sm">Half-day events organized by D&D Padel · teams of 2</p>
            </div>
          </div>
          {user?.role === "admin" && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-coral hover:bg-coral-600 text-white gap-1.5" data-testid="create-tournament-btn"><Plus size={16} /> New tournament</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Organize a tournament</DialogTitle><DialogDescription>Players register as teams of two. Confirm the event once teams are in to publish the order of play.</DialogDescription></DialogHeader>
                <TournamentForm onSaved={(t) => { setCreateOpen(false); navigate(`/tournaments/${t._id}`); }} />
              </DialogContent>
            </Dialog>
          )}
        </div>

        {upcoming.length === 0 && (
          <div className="text-center py-20 rounded-3xl border border-dashed border-border/50" data-testid="no-tournaments">
            <Trophy size={48} className="mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-muted-foreground text-lg mb-1">No upcoming tournament</p>
            <p className="text-muted-foreground text-sm">D&D Padel announces its next event here — stay tuned!</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {upcoming.map((t, i) => <TournamentCard key={t._id} t={t} i={i} />)}
        </div>

        {past.length > 0 && (
          <>
            <p className="text-xs uppercase tracking-wider text-muted-foreground pt-10 pb-3">Past events</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-80">
              {past.map((t, i) => <TournamentCard key={t._id} t={t} i={i} />)}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

function TournamentCard({ t, i }) {
  const st = TOURNAMENT_STATUS[t.status] || TOURNAMENT_STATUS.open;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
      <Link to={`/tournaments/${t._id}`} data-testid="tournament-card">
        <Card className="border-border/50 card-lift h-full">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3 gap-2">
              <h3 className="font-heading font-bold text-base leading-tight">{t.name}</h3>
              <Badge className={`rounded-full text-[10px] shrink-0 ${st.cls}`}>{st.label}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 text-xs text-muted-foreground mb-3">
              <span className="flex items-center gap-1"><Calendar size={12} /> {t.date}</span>
              <span className="flex items-center gap-1"><Clock size={12} /> {t.start_time}–{t.end_time}</span>
              <span className="flex items-center gap-1"><Banknote size={12} /> Rs {t.price_per_person}/person</span>
              <span className="flex items-center gap-1"><Users size={12} /> {t.teams?.length || 0}/{t.max_teams} teams</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              {t.prize ? <span className="flex items-center gap-1 text-golden font-semibold"><Gift size={12} /> {t.prize}</span> : <span />}
              {t.champion ? <span className="flex items-center gap-1 text-golden font-semibold"><Crown size={12} /> {t.champion.name}</span> : <ChevronRight size={14} className="text-muted-foreground" />}
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
}
